import { Promotion, PromotionDateRange } from '../../graphql/promotions'
import { basisPointsToPercent } from '../../helpers/promotions'
import { BuilderForm, DateRangeValue } from './types'

export const emptyForm: BuilderForm = {
  name: '',
  providerCode: '',
  discountType: 'Percent',
  discountValue: '',
  currency: 'IRR',
  codeMode: 'None',
  code: '',
  distribution: 'AutoApplied',
  advertisementOptIn: false,
  propertyCodes: [],
  roomTypeCodes: [],
  ratePlanCodes: [],
  channelCodes: '',
  consumableVia: [],
  stayRanges: [],
  bookingRanges: [],
  blackoutRanges: [],
  daysOfWeek: [],
  advancePurchaseMinDays: '',
  advancePurchaseMaxDays: '',
  maxRedemptions: '',
  maxDiscountBudgetMinor: '',
  maxRedemptionsPerGuest: '',
  stackable: false,
  priority: '0'
}

/**
 * API ranges to form ranges.
 *
 * EVERY range, not the first. The form used to read `includeRanges[0]` and write a
 * single-element array, so a promotion with two stay periods showed one and lost the other
 * on the next save — including a save that only renamed it. Nothing below the form ever had
 * that limit: the column is jsonb, the proto field is `repeated`, and the evaluator
 * iterates the whole list.
 */
export const toRangeValues = (ranges: PromotionDateRange[]): DateRangeValue[] =>
  (ranges || []).map((range) => ({
    start: range.start?.slice(0, 10) || '',
    end: range.end?.slice(0, 10) || ''
  }))

/** A day-boundary ISO timestamp, which is what the API accepts for a date range. */
export const toDateRangeValue = (value: string, endOfDay = false) =>
  value ? `${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}Z` : value

/**
 * Form ranges back to API ranges.
 *
 * Half-filled rows are dropped rather than sent: a row where the operator picked a start
 * and has not yet picked an end is an unfinished edit, not a range, and the API would
 * reject the whole mutation for it. Validation reports it separately — this function is
 * only reached once validation has passed.
 */
export const toApiRanges = (ranges: DateRangeValue[]) =>
  ranges
    .filter((range) => range.start && range.end)
    .map((range) => ({
      start: toDateRangeValue(range.start),
      end: toDateRangeValue(range.end, true)
    }))

/** A whole-number field, or undefined when the operator left it blank (= no cap). */
export const toOptionalInt = (value: string): number | undefined => {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : undefined
}

/** A whole-number field kept as a string, because the API takes int64 money as a string. */
export const toOptionalIntString = (value: string): string | undefined => {
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

export const toForm = (promotion: Promotion): BuilderForm => ({
  name: promotion.name,
  providerCode: promotion.providerCode,
  discountType: promotion.discountType,
  // Percent discounts are stored in BASIS POINTS (2000 = 20%) and were previously shown
  // raw, so a 20% promotion read as "2000" beside a field labelled Percent. FixedAmount
  // stays raw: it is minor units, and money is displayed unconverted throughout this app.
  discountValue:
    promotion.discountType === 'Percent'
      ? basisPointsToPercent(promotion.discountValue)
      : promotion.discountValue,
  currency: promotion.currency,
  codeMode: promotion.codeMode,
  code: promotion.code ?? '',
  // CORRECTED ON LOAD, not only when the operator edits the code mode.
  //
  // Rows holding the invalid pair already exist — they are exactly what the loyalty
  // migration is written to surface. Correcting only in the code-mode onChange left them
  // reachable: load one, rename it, save, and BulkVoucher + AutoApplied is re-sent
  // untouched. The server now rejects that, so nothing corrupt persists — but the
  // operator gets an opaque "unable to save" toast for a field they never touched, which
  // is a bad way to learn about it.
  //
  // Correcting here also makes the screen honest for someone who is only looking: the
  // Distribution field shows what the promotion must be, rather than a value that no
  // longer has a selectable menu entry.
  distribution:
    promotion.codeMode !== 'None' ? 'CodeEntry' : promotion.distribution,
  advertisementOptIn:
    promotion.distribution === 'CodeEntry' &&
    (promotion.advertisementOptIn ?? false),

  propertyCodes: promotion.scope?.propertyCodes ?? [],
  roomTypeCodes: promotion.scope?.roomTypeCodes ?? [],
  ratePlanCodes: promotion.scope?.ratePlanCodes ?? [],
  channelCodes: (promotion.scope?.channelCodes ?? []).join(', '),
  consumableVia: promotion.consumableVia ?? [],

  stayRanges: toRangeValues(promotion.schedule?.includeRanges ?? []),
  bookingRanges: toRangeValues(promotion.schedule?.bookingWindowRanges ?? []),
  blackoutRanges: toRangeValues(promotion.schedule?.blackoutRanges ?? []),
  daysOfWeek: promotion.schedule?.daysOfWeek ?? [],
  advancePurchaseMinDays:
    promotion.schedule?.advancePurchaseMinDays == null
      ? ''
      : String(promotion.schedule.advancePurchaseMinDays),
  advancePurchaseMaxDays:
    promotion.schedule?.advancePurchaseMaxDays == null
      ? ''
      : String(promotion.schedule.advancePurchaseMaxDays),

  maxRedemptions: promotion.caps?.maxRedemptions ?? '',
  maxDiscountBudgetMinor: promotion.caps?.maxDiscountBudgetMinor ?? '',
  maxRedemptionsPerGuest:
    promotion.caps?.maxRedemptionsPerGuest == null
      ? ''
      : String(promotion.caps.maxRedemptionsPerGuest),

  stackable: promotion.stackable,
  priority: String(promotion.priority),
  updatedAt: promotion.updatedAt
})
